import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

/**
 * SplashIntro — opens with a centered k/ai logo, then morphs it into
 * the navigation logo (top-left) as the dark backdrop fades away.
 * Plays on every page load.
 *
 * Phases:
 *  intro (0  → 1.4s): logo centered, dark backdrop, caption visible
 *  morph (1.4 → 2.3s): logo flies + scales to nav-logo position, backdrop fades to 0
 *  done            : component unmounts; the real nav logo is already in place
 */
const SPLASH_SIZE = 120;

const SplashIntro = () => {
  const [phase, setPhase] = useState("intro");
  const [target, setTarget] = useState(null);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1200,
    h: typeof window !== "undefined" ? window.innerHeight : 800,
  }));

  // Sync body data attribute so Navigation can fade in via CSS
  useEffect(() => {
    if (phase === "done") {
      delete document.body.dataset.splash;
    } else {
      document.body.dataset.splash = phase;
    }
    return () => {
      delete document.body.dataset.splash;
    };
  }, [phase]);

  // Phase transitions
  useEffect(() => {
    if (phase === "intro") {
      const t = setTimeout(() => {
        // Measure the target nav logo position
        const navLogo = document.querySelector(
          '[data-testid="nav-logo"] [data-testid="kai-logo"]'
        );
        if (navLogo) {
          const r = navLogo.getBoundingClientRect();
          setTarget({
            cx: r.left + r.width / 2,
            cy: r.top + r.height / 2,
            size: r.width,
          });
        }
        setViewport({ w: window.innerWidth, h: window.innerHeight });
        setPhase("morph");
      }, 1400);
      return () => clearTimeout(t);
    }
    if (phase === "morph") {
      const t = setTimeout(() => setPhase("done"), 900);
      return () => clearTimeout(t);
    }
  }, [phase]);

  if (phase === "done") return null;

  const cxStart = viewport.w / 2;
  const cyStart = viewport.h / 2;
  const isMorphing = phase === "morph" && target;

  // Logo position uses x/y as the CENTER of the logo box
  const logoAnimate = isMorphing
    ? {
        x: target.cx - SPLASH_SIZE / 2,
        y: target.cy - SPLASH_SIZE / 2,
        scale: target.size / SPLASH_SIZE,
      }
    : {
        x: cxStart - SPLASH_SIZE / 2,
        y: cyStart - SPLASH_SIZE / 2,
        scale: 1,
      };

  return (
    <motion.div
      data-testid="splash-intro"
      className="fixed inset-0 z-[100]"
      style={{ pointerEvents: "none" }}
      initial={{ backgroundColor: "rgba(10,10,11,1)" }}
      animate={{
        backgroundColor: isMorphing
          ? "rgba(10,10,11,0)"
          : "rgba(10,10,11,1)",
      }}
      transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Subtle grain — only during intro */}
      <motion.div
        aria-hidden
        className="absolute inset-0 mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
        initial={{ opacity: 0.3 }}
        animate={{ opacity: isMorphing ? 0 : 0.3 }}
        transition={{ duration: 0.5 }}
      />

      {/* Flying logo */}
      <motion.div
        className="absolute top-0 left-0"
        style={{
          width: SPLASH_SIZE,
          height: SPLASH_SIZE,
          transformOrigin: "center center",
        }}
        initial={{
          x: cxStart - SPLASH_SIZE / 2,
          y: cyStart - SPLASH_SIZE / 2,
          scale: 0.85,
          opacity: 0,
        }}
        animate={{
          ...logoAnimate,
          opacity: 1,
        }}
        transition={{
          duration: isMorphing ? 0.85 : 0.7,
          ease: isMorphing ? [0.7, 0, 0.25, 1] : [0.22, 1, 0.36, 1],
          delay: isMorphing ? 0 : 0.05,
        }}
      >
        <svg
          width={SPLASH_SIZE}
          height={SPLASH_SIZE}
          viewBox="0 0 64 64"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Tile */}
          <rect
            x="0.5"
            y="0.5"
            width="63"
            height="63"
            rx="14"
            ry="14"
            fill="#0f0f10"
            stroke="rgba(255,255,255,0.12)"
            strokeWidth="1"
          />
          {/* Big star — fast spin then settle */}
          <motion.g
            initial={{ rotate: -180, scale: 0.3, opacity: 0 }}
            animate={{ rotate: 0, scale: 1, opacity: 1 }}
            transition={{
              duration: 1.0,
              ease: [0.22, 1, 0.36, 1],
              delay: 0.2,
            }}
            style={{ transformOrigin: "53px 13px" }}
          >
            <path
              d="M 53 6 L 55 11 L 60 13 L 55 15 L 53 20 L 51 15 L 46 13 L 51 11 Z"
              fill="#00BCD4"
            />
          </motion.g>
          {/* Companion star */}
          <motion.path
            d="M 40 4 L 41 6.5 L 43.5 8 L 41 9.5 L 40 12 L 39 9.5 L 36.5 8 L 39 6.5 Z"
            fill="#00BCD4"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 0.95, 0.7], scale: [0, 1.3, 1] }}
            transition={{ duration: 0.7, delay: 0.6 }}
            style={{ transformOrigin: "40px 8px" }}
          />
          {/* Wordmark */}
          <motion.text
            x="32"
            y="44"
            textAnchor="middle"
            fontFamily="ui-monospace, SFMono-Regular, 'JetBrains Mono', Menlo, monospace"
            fontSize="22"
            fontWeight="600"
            letterSpacing="-1"
            fill="#ffffff"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.45 }}
          >
            k<tspan fill="#00BCD4" dx="-0.5">/</tspan>
            <tspan dx="-1.5">ai</tspan>
          </motion.text>
        </svg>
      </motion.div>

      {/* Caption — fades out as soon as morph begins */}
      <div
        className="absolute left-1/2 flex justify-center pointer-events-none"
        style={{
          top: "50%",
          transform: `translate(-50%, ${SPLASH_SIZE / 2 + 32}px)`,
        }}
      >
        <motion.div
          className="flex flex-col items-center gap-2"
          initial={{ opacity: 0, y: 12 }}
          animate={{
            opacity: isMorphing ? 0 : 1,
            y: isMorphing ? -8 : 0,
          }}
          transition={{
            duration: isMorphing ? 0.35 : 0.5,
            delay: isMorphing ? 0 : 0.75,
            ease: "easeOut",
          }}
        >
          <span className="text-[10px] font-mono tracking-[0.4em] uppercase text-white/40">
            Keimyung University
          </span>
          <span className="text-sm font-light tracking-tight text-white/90">
            AI Research Center
          </span>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default SplashIntro;
